/**
 * Nosto recommendation carousel.
 *
 * Nosto injects its recommendation markup asynchronously (after page load, and
 * again on some re-renders such as variant changes), so we can't just query for
 * the elements once on DOMContentLoaded. Instead we watch the DOM for any
 * `.nosto-swiper` container that Nosto adds and initialise Swiper on it.
 *
 * The markup + scoped styles live in `nosto/product-carousel.html` (the Nosto
 * template). This file owns the carousel behaviour and the card add-to-cart handler.
 */

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { recommendationsEnabled } from './_nosto-flags.js';
import { resolveNostoSurface, recordNostoAddToCart } from './_nosto-attribution.js';

const SELECTOR = '.nosto-swiper';

function initCarousel(el) {
  if (!el || el.dataset.swiperInitialized) return;
  el.dataset.swiperInitialized = 'true';

  // Arrows live on .rec-swiper (one level above .swiper) so they can straddle the
  // carousel edge without being clipped by .swiper's overflow:hidden — scope the
  // navigation lookup there, falling back to el if the wrapper isn't present.
  const scope = el.closest('.rec-swiper') || el;

  const swiper = new Swiper(el, {
    modules: [Navigation],
    slidesPerView: 2,
    spaceBetween: 40, // gap BETWEEN slides only (no outer-edge overhang); tweak to taste
    watchOverflow: true, // hide arrows / disable dragging when everything fits
    navigation: {
      prevEl: scope.querySelector('.swiper-button-prev'),
      nextEl: scope.querySelector('.swiper-button-next'),
    },
    breakpoints: {
      1024: { slidesPerView: 3 }, // matches the Rebuy block: 2 up to desktop, 3 above
    },
  });

  observeCardLoads(el, swiper);
}

/**
 * Slides now contain <nosto-dynamic-card> elements that fetch their real markup
 * asynchronously (a skeleton placeholder renders first). Swiper measures slide
 * heights at init — i.e. against the skeletons — so once the real cards inject we
 * must re-measure or the track height/arrows can be wrong.
 *
 * The dynamic-card web component doesn't expose a stable public "loaded" event, so
 * we watch the wrapper subtree for the injected content and debounce a
 * swiper.update() across the burst of mutations (all cards resolving at once).
 */
function observeCardLoads(el, swiper) {
  const wrapper = el.querySelector('.swiper-wrapper');
  if (!wrapper) return;

  let raf = null;
  const cardObserver = new MutationObserver(() => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => swiper.update());
  });

  cardObserver.observe(wrapper, { childList: true, subtree: true });
}

function initWithin(node) {
  if (node.nodeType !== 1) return;
  if (node.matches && node.matches(SELECTOR)) initCarousel(node);
  if (node.querySelectorAll) node.querySelectorAll(SELECTOR).forEach(initCarousel);
}

/**
 * Record the add-to-cart for the fallback path in initAddToCart() below.
 *
 * `window.Nosto.addProductToCart` looks like it handles its own attribution — it does post
 * to /attribution/record — but it records a `vp` (a click-through), never the `cp` an add
 * to cart needs. Nothing else covers this path either: it never dispatches `variant:add`,
 * so _nosto-attribution.js's listener never sees it. Result: adds made from a Nosto card
 * whose theme button wasn't available were tracked as views, and Nosto's cart product
 * event simply never arrived.
 *
 * The one awkward part is the product id. The Nosto template's button carries only
 * `data-handle`, and `addProductToCart` resolves `undefined`, so there is nothing to read
 * it from — hence this /cart.js round-trip to look the line up by handle. It runs after
 * the add and after the `cart:refresh` dispatch, so it never delays the drawer.
 *
 * If the same product sits in the cart under several variants there will be several lines
 * with this handle; any of them gives the right `product_id`, which is what `cp`
 * attribution targets. `variant_id` is a best effort for the search-surface call, which
 * these buttons don't currently reach — they only ever render inside reco placements.
 */
async function recordAddToCart(surface, handle) {
  if (!surface) return; // resolveNostoSurface already logged why

  try {
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    const line = (cart.items || []).find((item) => item.handle === handle);
    if (!line) {
      console.warn('[nosto] added "' + handle + '" but no matching cart line — not attributing');
      return;
    }
    recordNostoAddToCart(surface, line.product_id, line.variant_id);
  } catch (err) {
    console.warn('[nosto] could not read /cart.js to attribute the add', err);
  }
}

/**
 * Add-to-cart for the Nosto recommendation cards.
 *
 * The card visuals come from the theme via <nosto-dynamic-card>, but the button lives
 * in the Nosto template (carrying data-handle) so it can be styled and copy-controlled
 * from the template.
 *
 * Preferred path: forward the click to the theme card's own quick-add button. That runs
 * the theme's real add flow, which dispatches `variant:add` — the event
 * _nosto-attribution.js listens on — so attribution happens in exactly one place for
 * every Nosto surface, rather than this file self-attributing via addProductToCart. It
 * also means multi-variant products open the theme's size modal instead of silently
 * adding a default variant. The theme owns the button state and the cart refresh on this
 * path, so we don't touch either.
 *
 * Fallback path: `window.Nosto.addProductToCart(handle, btn, 1)`. This is NOT dead code —
 * it's the live path whenever there's no theme button to forward to:
 *   - the theme only renders quick-add under `show_quick_buy and product.available` and
 *     `market_oos == false` (snippets/product-card.liquid), so an out-of-stock or
 *     quick-buy-disabled card has no button and would otherwise do nothing at all;
 *   - the dynamic card may not have resolved yet when the click lands. They're lazy, and
 *     on a normal page load only about a third of a carousel's cards have resolved;
 *   - nosto/cart-drawer-suggestions.html hand-builds its compact rows with no
 *     <nosto-dynamic-card> and no .swiper-slide, so it always lands here — which is why
 *     the theme-button lookup is scoped to the slide, and why the "Adding…" state and the
 *     `cart:refresh` dispatch must stay on this branch.
 *
 * This path used to be described here as self-attributing. It isn't. `addProductToCart`
 * does post to Nosto, but it records a `vp` — a click-through — not the `cp` an add to
 * cart needs, and it never dispatches `variant:add`, so _nosto-attribution.js's listener
 * never sees it either. Nosto reported cart product events going missing, intermittently
 * and unpredictably; this was why, and which of the three bullets above you hit decided
 * whether the event arrived. recordAddToCart() below closes it.
 *
 * `cart:refresh` on documentElement is the same idiom the theme uses for programmatic
 * adds (see _wishlist_add_to_cart_fix.js); with cart_type "drawer" it refreshes the
 * drawer/header cart contents.
 *
 * Delegated on `document` because Nosto injects the cards asynchronously. Bound once.
 */
let addToCartBound = false;
function initAddToCart() {
  if (addToCartBound) return;
  addToCartBound = true;

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-nosto-add]');
    if (!btn) return;
    e.preventDefault();

    // Scoped to the slide so cart-drawer suggestions (no .swiper-slide) can't reach a
    // neighbouring card's button.
    const slide = btn.closest('.swiper-slide');
    const themeBtn =
      slide && slide.querySelector('nosto-dynamic-card product-card .product-card__quick-add-button');
    if (themeBtn) {
      themeBtn.click();
      return;
    }

    const handle = btn.dataset.handle;
    const add = window.Nosto && window.Nosto.addProductToCart;
    if (!handle || typeof add !== 'function') {
      if (typeof add !== 'function') console.warn('[nosto] addProductToCart unavailable');
      return;
    }
    if (btn.dataset.adding) return; // guard against double-clicks

    // INVARIANT: resolve the surface HERE, synchronously, while the button is definitely
    // still in the placement. Everything below is asynchronous, and a cart event can
    // re-render the placement out from under us — a detached button resolves to no
    // surface, which would silently drop the attribution. Same reasoning as the
    // quick-buy `pending` capture in _nosto-attribution.js.
    const surface = resolveNostoSurface(btn);

    const label = btn.textContent;
    btn.dataset.adding = 'true';
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      // Nosto Shopify helper: (productHandle, element, quantity). Returns a Promise.
      await add(handle, btn, 1);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      await recordAddToCart(surface, handle);
    } catch (err) {
      console.error('[nosto] add to cart failed', err);
    } finally {
      btn.disabled = false;
      btn.textContent = label;
      delete btn.dataset.adding;
    }
  });
}

function start() {
  initAddToCart();

  // Catch anything already in the DOM (e.g. a fast Nosto render).
  document.querySelectorAll(SELECTOR).forEach(initCarousel);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(initWithin);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// Off unless the "Nosto recommendations" theme setting is on. The module stays in the
// bundle either way, so the setting alone flips it — no rebuild. See _nosto-flags.js.
if (recommendationsEnabled()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}
